import Dexie, { type Table } from 'dexie';
import type { Unit } from './countdown';

export type Kind = 'birthday' | 'anniversary' | null;
/// The four units iOS exposes as compact-format choices (the segmented control
/// in CreateOrEditCountdown.storyboard: "Pick Compact Format" → Days/Weeks/
/// Months/Years). DateFormattingUtils.compactTimeLeftFrom cascades down to
/// smaller units (Hour/Minute/Second) automatically when the chosen unit
/// rounds to 0, so we don't expose those in the picker.
export type CompactUnit = Extract<Unit, 'Day' | 'Week' | 'Month' | 'Year'>;

export interface Countdown {
  id: string;
  title: string;
  emoji: string;
  /// ISO 8601. An all-day countdown is stored at local midnight of its day.
  dueDate: string;
  /// All-day vs a deliberate time of day (even midnight) — mirrors iOS's
  /// Countdown.isAllDay and the `a` share-link key. Decides whether the
  /// clock shows; the counting itself uses dueDate.
  allDay: boolean;
  colorHex: number; // RRGGBB packed into an int (matches the `c` query param)
  kind: Kind;
  originYear: number | null;
  formatUnits: number; // iOS DateUnit bitmask — see countdown.ts
  /// Skip the viewer's weekend days in the Day figure — mirrors iOS's
  /// Countdown.countOnlyWeekdays (the `w` share-link key). Absent on rows
  /// from before the field, which reads as false.
  countOnlyWeekdays?: boolean;
  /// Unit shown big on the list cell — mirrors iOS's
  /// Countdown.compactDisplayUnit (defaults to Day, same as iOS).
  compactUnit: CompactUnit;
  createdAt: number;
  updatedAt: number;
}

class CountdownDB extends Dexie {
  countdowns!: Table<Countdown, string>;
  constructor() {
    super('countdown-pwa');
    this.version(1).stores({
      countdowns: 'id, updatedAt',
    });
    /// v2: add `compactUnit` defaulting to 'Day' for any pre-existing row.
    /// Same default as iOS's Countdown.compactDisplayUnit so upgrades match
    /// what the user would have seen had compactUnit existed from day one.
    this.version(2)
      .stores({ countdowns: 'id, updatedAt' })
      .upgrade((tx) =>
        tx.table('countdowns').toCollection().modify((c: Countdown) => {
          if (!c.compactUnit) c.compactUnit = 'Day';
        }),
      );
    /// v3: add `allDay`, from the rule that decided it until now: local
    /// midnight meant all-day. Same fallback iOS applies to rows from before
    /// its own flag.
    this.version(3)
      .stores({ countdowns: 'id, updatedAt' })
      .upgrade((tx) =>
        tx.table('countdowns').toCollection().modify((c: Countdown) => {
          if (typeof c.allDay !== 'boolean') c.allDay = isLocalMidnight(new Date(c.dueDate));
        }),
      );
  }
}

export const db = new CountdownDB();

/// The legacy all-day rule, for records that carry no flag.
export function isLocalMidnight(d: Date): boolean {
  return d.getHours() === 0 && d.getMinutes() === 0;
}

export async function listCountdowns(): Promise<Countdown[]> {
  return db.countdowns.toArray();
}

/// Mirrors Countdown.swift:sortCountdownsFromSoonestToFurthest (CountdownApp
/// model). Future countdowns come first ordered soonest → furthest; past
/// countdowns are grouped at the end ordered most-recent-past first. The
/// past cutoff is midnight of "today" so a same-day countdown still sorts
/// as future until the calendar day flips.
///
/// `now` is exposed so callers can drive a live re-sort: pass a ticking
/// state and the order updates the moment a countdown crosses midnight
/// from "today" into "past" — without this the initial sort would freeze
/// the list until the next reload.
export function sortSoonestToFurthest(items: Countdown[], now: Date = new Date()): Countdown[] {
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  const midnightMs = midnight.getTime();
  return [...items].sort((a, b) => {
    const aDue = new Date(a.dueDate).getTime();
    const bDue = new Date(b.dueDate).getTime();
    const aPast = aDue < midnightMs;
    const bPast = bDue < midnightMs;
    if (aPast !== bPast) return aPast ? 1 : -1;
    return aPast ? bDue - aDue : aDue - bDue;
  });
}

export async function getCountdown(id: string): Promise<Countdown | undefined> {
  return db.countdowns.get(id);
}

export async function saveCountdown(c: Countdown): Promise<void> {
  await db.countdowns.put(c);
}

export async function deleteCountdown(id: string): Promise<void> {
  await db.countdowns.delete(id);
}
